// Parte neutra do tema (sem React): a chave de storage, as cores de barra e o parser. O
// provider e o hook ficam em `components/app/theme.tsx`.
//
// O painel é uma SPA (sem server render), então a preferência mora em `localStorage` e
// quem pinta a classe `.dark` antes do primeiro quadro é o script inline de `index.html`.
// Os literais daquele script precisam bater com os daqui.

export type ThemePref = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'leztypay.theme';

/** Cor da barra do navegador por tema (`meta[name=theme-color]`). */
export const THEME_COLOR = { light: '#F4F7F7', dark: '#0F1517' } as const;

/**
 * Default do produto: CLARO. Quem não escolheu nada vê o tema claro mesmo com o sistema em
 * escuro; "sistema" continua sendo opção do seletor, só não é o padrão.
 */
export const THEME_DEFAULT: ThemePref = 'light';

/** Valida o que veio do storage; valor estranho ou ausente cai no default. */
export function parseThemePref(raw: string | null | undefined): ThemePref {
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : THEME_DEFAULT;
}

/** `true` quando o sistema operacional está em escuro. */
export function prefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Resolve a preferência para o que vai de fato para a tela. */
export function resolveTheme(pref: ThemePref): 'light' | 'dark' {
  if (pref === 'system') {
    return prefersDark() ? 'dark' : 'light';
  }
  return pref;
}

/** Aplica o tema no documento: classe `.dark` e cor da barra do navegador. */
export function applyTheme(resolved: 'light' | 'dark'): void {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', THEME_COLOR[resolved]);
}

/** Lê a preferência gravada. `localStorage` pode lançar (modo privado, cookies bloqueados). */
export function readThemePref(): ThemePref {
  try {
    return parseThemePref(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return THEME_DEFAULT;
  }
}

/** Grava a preferência. Falha em silêncio: o tema da sessão atual já foi aplicado. */
export function writeThemePref(pref: ThemePref): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // storage indisponível: a escolha vale só nesta sessão.
  }
}
